import { useMemo } from "react";
import {
  Box,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { PerformancePeriod, TableSort } from "../../../../../features/optifundDashboardSlice";
import { formatCurrency, formatDisplayDate, formatInteger, formatPercent } from "../formatters";
import { PerformanceRow } from "./performanceApiMapping";

export const PERIOD_LABELS: Record<string, string> = {
  daily: "Last 30 Days",
  weekly: "Last 6 Weeks",
  monthly: "Last 12 Months",
};

const TABLE_HEADERS = [
  { key: "date", label: "Date", align: "left" as const },
  { key: "impressions", label: "Impressions", align: "right" as const },
  { key: "clicks", label: "Click in", align: "right" as const },
  { key: "spend", label: "Spend", align: "right" as const },
  { key: "cpc", label: "CPC", align: "right" as const },
  { key: "leads", label: "Leads", align: "right" as const },
  { key: "matched", label: "Matched Leads", align: "right" as const },
  { key: "qualified", label: "Qualified Leads", align: "right" as const },
  { key: "paid", label: "Paid Leads", align: "right" as const },
  { key: "income", label: "Total income", align: "right" as const },
  { key: "profit", label: "Profit", align: "right" as const },
  { key: "cpq", label: "Cost / qual", align: "right" as const },
  { key: "convRate", label: "Conv rate", align: "right" as const },
];

interface PerformanceTableProps {
  rows: PerformanceRow[];
  period: PerformancePeriod;
  onPeriodChange: (period: PerformancePeriod) => void;
  page: number;
  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  sort: TableSort;
  onSortChange: (sort: TableSort) => void;
}

export default function PerformanceTable({
  rows,
  period,
  onPeriodChange,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  sort,
  onSortChange,
}: PerformanceTableProps) {
  const sortedRows = useMemo(() => {
    const { keytoSort, direction } = sort;
    return rows.slice().sort((a: any, b: any) => {
      const aValue = a[keytoSort];
      const bValue = b[keytoSort];
      if (typeof aValue === "string" && typeof bValue === "string") {
        return direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return direction === "asc" ? aValue - bValue : bValue - aValue;
    });
  }, [rows, sort]);

  const pagedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (key: string) => {
    onSortChange({
      keytoSort: key,
      direction: sort.keytoSort === key && sort.direction === "asc" ? "desc" : "asc",
    });
  };

  const handlePeriodChange = (_event: unknown, value: string | null) => {
    if (!value) return;
    onPeriodChange(value as PerformancePeriod);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange(+event.target.value);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, mb: 1.75 }}>
        <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 500, color: "#1a1a1a" }}>
          Account Summary - {PERIOD_LABELS[period]}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          color="secondary"
          value={period}
          onChange={handlePeriodChange}
          sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden" }}
        >
          <ToggleButton value="daily">Daily</ToggleButton>
          <ToggleButton value="weekly" data-tracker="974">
            Weekly
          </ToggleButton>
          <ToggleButton value="monthly" data-tracker="975">
            Monthly
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderColor: "#ececec" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <TableCell
                  key={header.key}
                  align={header.align}
                  sx={{ fontWeight: 700, color: "#8E24AA", backgroundColor: "#faf8fb", fontSize: 12 }}
                >
                  <TableSortLabel
                    active={sort.keytoSort === header.key}
                    direction={sort.keytoSort === header.key ? sort.direction : "asc"}
                    onClick={() => handleSort(header.key)}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((row) => (
              <TableRow key={row.date}>
                <TableCell sx={{ color: "#424242", fontSize: 13.5 }}>{formatDisplayDate(row.date)}</TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatInteger(row.impressions)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatInteger(row.clicks)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatCurrency(row.spend)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatCurrency(row.cpc)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatInteger(row.leads)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatInteger(row.matched)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatInteger(row.qualified)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatInteger(row.paid)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatCurrency(row.income)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 500,
                    fontSize: 13.5,
                    color: row.profit > 0 ? "#2E7D32" : row.profit < 0 ? "#C62828" : "#616161",
                  }}
                >
                  {formatCurrency(row.profit)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatCurrency(row.cpq)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatPercent(row.convRate)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack spacing={1} direction="row" justifyContent="space-between" alignItems="center">
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={sortedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-selectLabel": { display: "none" },
            ".MuiTablePagination-displayedRows": { display: "none" },
            ".MuiTablePagination-actions": { display: "none" },
            ".MuiTablePagination-toolbar": { paddingLeft: "16px" },
          }}
        />
        <Pagination
          count={Math.max(1, Math.ceil(sortedRows.length / rowsPerPage))}
          showFirstButton
          showLastButton
          color="secondary"
          size="small"
          page={page + 1}
          onChange={(_event, value) => handleChangePage(null, value - 1)}
        />
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={sortedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-displayedRows": { display: "none" },
            ".MuiTablePagination-actions": { display: "none" },
            ".MuiTablePagination-input": { marginRight: "15px" },
          }}
        />
      </Stack>
    </Box>
  );
}
